import { useQuery } from "@apollo/client";
import orderBy from "lodash-es/orderBy";
import { useMemo, useState } from "react";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import PageWrapper from "components/PageWrapper";
import PageInfo from "components/PageWrapper/Info";
import PageWrapperTitle from "components/PageWrapper/Title";
import NotFoundPage from "components/error/NotFoundPage";
import { InfoCircle } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { Stack } from "types/generated";
import { PhaseName, phaseHooks, phases, toCommands } from "utils/hooks";

import { StackContext } from "../Context";
import StackHeader from "../components/Header";
import { getStacksBackUrl } from "../helpers";
import { showStackHooksDocumentationDrawer } from "./components/DocumentationDrawer";
import PhaseSection from "./components/PhaseSection";
import { GET_STACK_HOOKS } from "./gql";
import { ContextPhaseHooks, Phases, StackPhaseHooks } from "./types";

const StackHooks = () => {
  const [changedHooks, setChangedHooks] = useState<Set<string>>(new Set());
  const { stack } = useTypedContext(StackContext);

  useTitle(`Hooks · ${stack.name}`);

  const { data, loading, error } = useQuery<{ stack: Stack }>(GET_STACK_HOOKS, {
    variables: {
      stackId: stack.id,
    },
    fetchPolicy: "no-cache",
  });

  const [stackHooks, contextHooks] = useMemo(() => {
    const stackHooks = {} as StackPhaseHooks;
    const contextHooks = {} as ContextPhaseHooks;

    for (const phase in phases) {
      const phaseName = phase as PhaseName;
      const [beforeHookName, afterHookName] = phaseHooks[phaseName];

      stackHooks[phaseName] = {
        before: beforeHookName ? toCommands(data?.stack?.[beforeHookName]) : [],
        after: afterHookName ? toCommands(data?.stack?.[afterHookName]) : [],
      };

      contextHooks[phaseName] = {
        before: [],
        after: [],
      };

      for (const context of data?.stack?.attachedContexts || []) {
        const processHook = (hook: string) => ({
          text: hook,
          contextId: context.contextId,
          contextName: context.contextName,
          isAutoattached: context.isAutoattached,
          priority: context.priority,
        });

        if (beforeHookName) {
          contextHooks[phaseName].before = orderBy(
            contextHooks[phaseName].before.concat(
              context.contextHooks[beforeHookName].map(processHook)
            ),
            ["isAutoattached", "priority", "contextName"],
            ["asc", "asc", "desc"]
          );
        }

        if (afterHookName) {
          contextHooks[phaseName].after = orderBy(
            contextHooks[phaseName].after.concat(
              context.contextHooks[afterHookName].map(processHook)
            ),
            ["isAutoattached", "priority", "contextName"],
            ["desc", "desc", "asc"]
          );
        }
      }
    }

    return [stackHooks, contextHooks];
  }, [data?.stack]);

  useBreadcrumbs([
    {
      title: "Stacks",
      link: getStacksBackUrl(),
    },
    {
      title: stack.name,
    },
  ]);

  const handleOpenDocsDrawer = () => {
    showStackHooksDocumentationDrawer();
  };

  const processedPhases = useMemo(() => {
    const result: Phases = [];

    for (const [key, label] of Object.entries(phases)) {
      const name = key as PhaseName;
      const [beforeHookName, afterHookName] = phaseHooks[name];
      result.push({
        name,
        label,
        stackHooks: stackHooks[name],
        contextHooks: contextHooks[name],
        hasAfterHooks: afterHookName !== null,
        hasBeforeHooks: beforeHookName !== null,
      });
    }

    return result;
  }, [stackHooks, contextHooks]);

  const ErrorContent = useErrorHandle(error);

  useBlockNavigationModal({
    isNavigationBlocked: changedHooks.size > 0,
  });

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.stack) {
    return <PageLoading />;
  }

  if (!data?.stack) {
    return <NotFoundPage />;
  }

  const title = (
    <Box align="center" gap="medium">
      <PageWrapperTitle>Hooks</PageWrapperTitle>
      <ButtonIcon icon={InfoCircle} onPress={handleOpenDocsDrawer} variant="ghost">
        Open information drawer
      </ButtonIcon>
    </Box>
  );

  return (
    <>
      <StackHeader />
      <PageInfo title={title} />
      <PageWrapper gap="medium">
        {processedPhases.map((phase) => (
          <PhaseSection setChangedHooks={setChangedHooks} key={phase.name} {...phase} />
        ))}
      </PageWrapper>
    </>
  );
};

export default StackHooks;
